import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Alert,
  CircularProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar
} from '@mui/material';
import {
  FamilyRestroom as FamilyIcon,
  ContentCopy as CopyIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Link as LinkIcon,
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { familyApi } from '../api/apiService';
import { Family, FamilyInvitation, Parent, User } from '../types/models';

interface FamilyData {
  family: Family | null;
  members: Parent[];
  kids: User[];
}

const FamilyManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const [familyData, setFamilyData] = useState<FamilyData | null>(null);
  const [invitations, setInvitations] = useState<FamilyInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [generatedInvite, setGeneratedInvite] = useState<{
    invitation: FamilyInvitation;
    shareableLink: string;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: ''
  });

  useEffect(() => {
    loadFamilyData();
  }, []);

  const loadFamilyData = async () => {
    try {
      setLoading(true);
      const [familyResponse, invitationsResponse] = await Promise.all([
        familyApi.getFamily(),
        familyApi.getInvitations()
      ]);
      const familyRes = familyResponse.data;
      setFamilyData(familyRes ? {
        family: familyRes.family || null,
        members: familyRes.members || [],
        kids: familyRes.kids || [],
      } : null);
      setInvitations(invitationsResponse.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load family data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvite = async () => {
    try {
      const response = await familyApi.createInvite();
      setGeneratedInvite(response.data);
      setInviteDialogOpen(true);
      // Refresh invitations list
      const invitationsResponse = await familyApi.getInvitations();
      setInvitations(invitationsResponse.data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate invite');
    }
  };

  const handleCopyLink = () => {
    if (generatedInvite) {
      const fullLink = `${window.location.origin}${generatedInvite.shareableLink}`;
      navigator.clipboard.writeText(fullLink);
      setSnackbar({ open: true, message: 'Link copied to clipboard!' });
      setInviteDialogOpen(false);
    }
  };

  const handleCopyInviteLink = (inviteCode: string) => {
    const fullLink = `${window.location.origin}/join-family/${inviteCode}`;
    navigator.clipboard.writeText(fullLink);
    setSnackbar({ open: true, message: 'Invitation link copied to clipboard!' });
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await familyApi.cancelInvitation(invitationId);
      setInvitations(invitations.filter(inv => inv.id !== invitationId));
      setSnackbar({ open: true, message: 'Invitation cancelled' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to cancel invitation');
    }
  };

  const getDaysRemaining = (expiresAt: Date | string) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      minHeight: '80vh',
    }}>
      <Box sx={{ flex: 1 }}>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Family Members Section */}
      <Card sx={{ mb: 3, borderRadius: 3 }}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                p: 1,
                display: 'flex'
              }}
            >
              <FamilyIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                Family Members
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {familyData?.members?.length || 0} parent{(familyData?.members?.length || 0) !== 1 ? 's' : ''}, {familyData?.kids?.length || 0} kid{(familyData?.kids?.length || 0) !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          {!familyData?.family ? (
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              Create a kid to start a family
            </Typography>
          ) : (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleGenerateInvite}
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
              }}
            >
              Invite Parent
            </Button>
          )}
        </Box>
        <CardContent>
          {familyData?.family && (familyData.members?.length ?? 0) > 0 ? (
            <List>
              {familyData.members.map((member, index) => (
                <ListItem
                  key={member.id}
                  divider={index < (familyData.members?.length ?? 0) - 1}
                  sx={{ px: 0 }}
                >
                  <ListItemAvatar>
                    <Avatar src={member.picture} sx={{ bgcolor: 'primary.light' }}>
                      {member.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={member.name}
                    secondary={member.email}
                  />
                  {familyData.family?.createdByParentId === member.id && (
                    <Chip label="Creator" size="small" color="primary" variant="outlined" />
                  )}
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              {familyData?.family ? 'No family members yet' : 'Create your first kid to start a family'}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Pending Invitations Section */}
      {familyData?.family && invitations.length > 0 && (
        <Card sx={{ mb: 3, borderRadius: 3 }}>
          <Box
            sx={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Box
              sx={{
                bgcolor: 'rgba(255,255,255,0.2)',
                borderRadius: 2,
                p: 1,
                display: 'flex'
              }}
            >
              <LinkIcon sx={{ color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', fontWeight: 700 }}>
                Pending Invitations
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                {invitations.length} active invitation{invitations.length !== 1 ? 's' : ''}
              </Typography>
            </Box>
          </Box>
          <CardContent>
            <List>
              {invitations.map((invitation, index) => (
                <ListItem
                  key={invitation.id}
                  divider={index < invitations.length - 1}
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        edge="end"
                        onClick={() => handleCopyInviteLink(invitation.inviteCode)}
                        sx={{ color: 'primary.main' }}
                        title="Copy invitation link"
                      >
                        <CopyIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        onClick={() => invitation.id && handleCancelInvitation(invitation.id)}
                        sx={{ color: 'error.main' }}
                        title="Cancel invitation"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemText
                    primary={`Code: ${invitation.inviteCode}`}
                    secondary={`Expires in ${getDaysRemaining(invitation.expiresAt)} day${getDaysRemaining(invitation.expiresAt) !== 1 ? 's' : ''}`}
                  />
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      )}

      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Invite Parent to Family
          <IconButton onClick={() => setInviteDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {generatedInvite && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Share this link with another parent to invite them to your family:
              </Typography>
              <TextField
                fullWidth
                value={`${window.location.origin}${generatedInvite.shareableLink}`}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <IconButton onClick={handleCopyLink}>
                      <CopyIcon />
                    </IconButton>
                  )
                }}
                sx={{ mt: 2 }}
              />
              <Alert severity="info" sx={{ mt: 2 }}>
                This invite link expires in 7 days. The invited parent will have access to all family kids.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button variant="contained" startIcon={<CopyIcon />} onClick={handleCopyLink}>
            Copy Link
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
      {/* Back Button */}
      <Box sx={{ textAlign: 'center', mt: 4, display: 'flex', justifyContent: 'center', gap: 2, pb: 4 }}>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)}>
          Back
        </Button>
      </Box>
      </Box>
    </Box>
  );
};

export default FamilyManagementPage;